import { Alert, Row, Spin } from 'antd'
import {
  DrilldownDataContext,
  useBufferedFilteredEntries,
} from 'features/drilldown'
import { usePanelContext } from 'features/drilldown/components/PanelContext'
import {
  useFocusItems,
  useGetPrecedingFilters,
} from 'features/drilldown/data/DrilldownFilterContext'
import {
  useFilteredIds,
  useGetLinkedIds,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import {
  OntologyRolodexEntryType,
  useHistogramQueries,
  useOntology,
} from 'features/dynamicOntology'
import {
  DefaultSiteViewContextProvider,
  useAggregateSiteData,
} from 'features/overview'
import { useRolodexHistogram } from 'features/rolodex'
import {
  getComponentToDisplayOnFeatureClick,
  MapInteractionAPIs,
  SiteViewMap,
} from 'features/siteMap'
import { MapFeaturePattern } from 'features/siteMapViz'
import first from 'lodash/first'
import flatten from 'lodash/flatten'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoSite, GenericPanelData, RolodexEntryType } from 'types'

const LATEST_DATA_SCHEMA_VERSION = 1

export const DEFAULT_SITE_PANEL_DATA: GenericPanelData = {
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  title: 'Site View',
  isPinned: true,
}

const AppContainer = styled.div<{
  $isFullscreen: boolean
  $isPresentationModeEnabled: boolean
}>`
  display: flex;
  flex-direction: row;
  // These numbers are a rough approximation to account for header height, card title height, etc.
  height: ${(props) => {
    if (props.$isFullscreen) return '100%'
    return props.$isPresentationModeEnabled
      ? 'calc(100vh - 190px)'
      : 'calc(100vh - 260px)'
  }};
  overflow: hidden;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`

// TODO(xdowns): At some point, we want to update this to:
//  -take only the latest inspection per unit
//  - Link from those to CDSs
// This will handle assets where we do multiple inspections
function useGetAssetsWithLinked(linkedType: OntologyRolodexEntryType): {
  isLoading: boolean
  data: ReadonlySet<string>
} {
  const {
    queries: [histoQuery],
    isLoading: queryLoading,
  } = useHistogramQueries({
    select: useMemo(
      () => [
        {
          field: 'id',
          type: 'unit',
          group_by: true,
          label: 'unit_id',
        },
        {
          field: 'id',
          type: linkedType,
          op: 'count',
          label: 'count',
        },
      ],
      [linkedType]
    ),
    filters: useGetPrecedingFilters(),
  })

  const { data, isLoading } = useRolodexHistogram(histoQuery)
  const result = useMemo(
    () =>
      flatten(data).map((rec) => ({
        unitId: rec['unit_id'] as string,
        count: rec['count'] as number,
      })),
    [data]
  )
  return useMemo(
    () => ({
      data: new Set(
        result.filter((rec) => rec.count > 0).map((rec) => rec.unitId)
      ),
      isLoading: queryLoading || isLoading,
    }),
    [isLoading, queryLoading, result]
  )
}

type InspectionStatus = 'not_inspected' | 'inspected' | 'scheduled'
const INSPECTION_STATUS_COLORS: Record<InspectionStatus, string> = {
  inspected: CantileverTheme.colors.SeaGreen.trim(),
  scheduled: '#BDC1F0',
  not_inspected: CantileverTheme.colors.grayscale[400].trim(),
}
const INSPECTION_LEGEND_LABELS: Record<InspectionStatus, string> = {
  inspected: 'Inspected',
  not_inspected: 'Not Inspected',
  scheduled: 'Scheduled',
}

const InspectionLegend: React.FC = () => {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '24px',
        left: '32px',
        backgroundColor: 'rgba(10, 10, 10, 0.8)',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}>
      {Object.entries(INSPECTION_STATUS_COLORS).map(([key, color]) => (
        <div
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
          <div
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: color,
              marginRight: '8px',
              borderRadius: '2px',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: '14px',
              color: CantileverTheme.colors.gray[300],
            }}>
            {INSPECTION_LEGEND_LABELS[key as InspectionStatus]}
          </span>
        </div>
      ))}
    </div>
  )
}

function useGetFlyToId(activeAssetIds: ReadonlyArray<string>): {
  flyToId: string | undefined
  isLoading: boolean
} {
  const [focusItems] = useFocusItems()
  const { entryTypeNames, isDescendant, findLowestType } = useOntology()
  const entryTypes = useMemo(
    () => Object.keys(entryTypeNames),
    [entryTypeNames]
  )
  // Pick the lowest entry type which:
  //   - has a focus value
  //   - is a descendant of 'unit'
  const focusType: OntologyRolodexEntryType | undefined = useMemo(
    () =>
      first(
        entryTypes
          .filter((type) => isDescendant(type, 'unit'))
          .toSorted((t1, t2) => {
            if (t1 === t2) return 0
            if (findLowestType([t1, t2]) === t1) {
              return -1
            }
            return 1
          })
          .filter((type) => !!focusItems?.get(type as RolodexEntryType)?.length)
      ),
    [entryTypes, findLowestType, focusItems, isDescendant]
  )
  const { links: linkedToUnit, isLoading } = useGetLinkedIds(
    focusItems?.get((focusType as RolodexEntryType) ?? RolodexEntryType.UNIT) ??
      [],
    'unit'
  )
  const findActiveFlyToId = useCallback(
    (ids: ReadonlyArray<string> | undefined) => {
      const potentials = ids?.filter((ft) => activeAssetIds.includes(ft))
      if (potentials?.length === 1) {
        return potentials[0]
      }
      return undefined
    },
    [activeAssetIds]
  )
  const flyToId: string | undefined = useMemo(() => {
    const inspectionLink = findActiveFlyToId([...linkedToUnit.values()])
    return inspectionLink
  }, [findActiveFlyToId, linkedToUnit])

  return useMemo(
    () => ({
      isLoading,
      flyToId,
    }),
    [flyToId, isLoading]
  )
}

/**
 * Figure out if we should be flying to a unit.
 * This will get any focus-items, and try to fly to them.
 * So far, in order of preference:
 *  - if you have a focused unit id that's in the active units, fly to it
 *  - Look at any focused inspections, and see if their unit is in the active units
 *  ... TODO expet to add more here, like indications etc
 */
function useFlyTo(activeAssetIds: ReadonlyArray<string>): {
  unitIdFlying: [
    string | undefined,
    Dispatch<SetStateAction<string | undefined>>,
  ]
  isLoading: boolean
} {
  const [unitIDFlyingTo, setUnitIDFlyingTo] = useState<string | undefined>(
    undefined
  )
  const { flyToId, isLoading: flyToLoading } = useGetFlyToId(activeAssetIds)
  useEffect(() => {
    if (flyToId) {
      setUnitIDFlyingTo(flyToId)
    }
  }, [flyToId, setUnitIDFlyingTo])

  useEffect(() => {}, [])
  return useMemo(
    () => ({
      unitIdFlying: [unitIDFlyingTo, setUnitIDFlyingTo],
      isLoading: flyToLoading,
    }),
    [flyToLoading, unitIDFlyingTo]
  )
}

export const RawSiteView: React.FC = () => {
  const { data: sites, isLoading: isLoadingSites } = useBufferedFilteredEntries(
    {
      entryType: 'site',
      limit: 2,
      dataKey: 'sites',
    }
  )

  const { ids: activeAssetIds, isLoading: unitsLoading } =
    useFilteredIds('unit')

  const { data: inspectedUnitIds, isLoading: inspectionsLoading } =
    useGetAssetsWithLinked('component-dataset')
  const { data: scheduledUnitIds, isLoading: scheduledLoading } =
    useGetAssetsWithLinked('inspection')

  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)
  const { isFullscreen } = usePanelContext()

  const selectedYear = new Date().getUTCFullYear()

  // These leads to some degree of duplication of data loading, but allows us to reuse all of the site view logic for displaying past inspections with minimal duplication of code.
  const aggregateDataFetch = useAggregateSiteData(sites[0]?.id ?? '')

  const getUnitDisplayProperties = useCallback(
    (unitId: string | null) => ({
      color:
        unitId && activeAssetIds.includes(unitId)
          ? inspectedUnitIds.has(unitId)
            ? INSPECTION_STATUS_COLORS['inspected']
            : scheduledUnitIds.has(unitId)
              ? INSPECTION_STATUS_COLORS['scheduled']
              : INSPECTION_STATUS_COLORS['not_inspected']
          : CantileverTheme.colors.grayscale[400],
      pattern: MapFeaturePattern.Flat,
    }),
    [activeAssetIds, inspectedUnitIds, scheduledUnitIds]
  )

  const {
    unitIdFlying: [unitIDFlyingTo, setUnitIDFlyingTo],
    isLoading: flyToLoading,
  } = useFlyTo(activeAssetIds)

  const mapInteractionAPIs = useMemo<MapInteractionAPIs>(
    () => ({
      unitIDFlyingTo,
      setUnitIDFlyingTo,
    }),
    [setUnitIDFlyingTo, unitIDFlyingTo]
  )

  const componentToDisplayOnFeatureClick = useMemo(() => {
    if (aggregateDataFetch.isMainLoading || sites.length !== 1) {
      return undefined
    }
    return getComponentToDisplayOnFeatureClick(
      aggregateDataFetch.aggregateSiteData,
      sites[0],
      aggregateDataFetch.isRlCalcLoading,
      selectedYear
    )
  }, [
    aggregateDataFetch.aggregateSiteData,
    aggregateDataFetch.isMainLoading,
    aggregateDataFetch.isRlCalcLoading,
    sites,
    selectedYear,
  ])

  if (isLoadingSites && sites.length === 0) {
    return (
      <Row justify='center' align='middle' style={{ height: '50px' }}>
        <Spin />
      </Row>
    )
  }

  if (sites.length !== 1) {
    return (
      <Alert
        title='There needs to be exactly one site in scope to see site view'
        type='info'
        showIcon
      />
    )
  }

  const site: GeckoSite = sites[0]

  return (
    <AppContainer
      $isFullscreen={isFullscreen ?? false}
      $isPresentationModeEnabled={isPresentationModeEnabled}>
      <DefaultSiteViewContextProvider currentSite={site}>
        <SiteViewMap
          site={site}
          getUnitDisplayProperties={getUnitDisplayProperties}
          componentToDisplayOnFeatureClick={componentToDisplayOnFeatureClick}
          mapInteractionAPIs={mapInteractionAPIs}
        />
        <InspectionLegend />
        {(isLoadingSites ||
          unitsLoading ||
          inspectionsLoading ||
          scheduledLoading ||
          flyToLoading ||
          aggregateDataFetch.isMainLoading ||
          aggregateDataFetch.isRlCalcLoading) && (
          <LoadingOverlay>
            <Spin />
          </LoadingOverlay>
        )}
      </DefaultSiteViewContextProvider>
    </AppContainer>
  )
}

export const SiteView = React.memo(RawSiteView)
